import type { ButtonHTMLAttributes, ReactNode } from "react";

/**
 * Botão de ícone único.
 *
 * `label` é obrigatório e vira nome acessível e `title` (AGENTS §13.1, §17):
 * um ícone sozinho sem nome é regressão conhecida, não escolha estética. O
 * ícone em si é ocultado da árvore acessível.
 */
export interface IconButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type" | "children" | "aria-label"> {
  label: string;
  icon: ReactNode;
  size?: "default" | "compact";
  variant?: "default" | "bare";
  /** Estado ligado de um filtro ou painel; reflete `aria-pressed`. */
  active?: boolean;
  /** Motivo da indisponibilidade; entra no nome acessível e no `title` (§18). */
  disabledReason?: string;
  type?: "button" | "submit" | "reset";
}

export function IconButton({
  label,
  icon,
  size = "default",
  variant = "default",
  active,
  disabledReason,
  type = "button",
  className,
  ...rest
}: IconButtonProps) {
  // Um controle desabilitado precisa dizer o motivo, e o ícone não diz nada
  // sozinho: o motivo entra no nome acessível e na dica (AGENTS §18).
  const name = rest.disabled && disabledReason ? `${label} — ${disabledReason}` : label;
  return (
    <button
      {...rest}
      type={type}
      className={className ? `cx-icon-btn ${className}` : "cx-icon-btn"}
      data-size={size}
      data-variant={variant}
      data-active={active ? "true" : undefined}
      aria-label={name}
      aria-pressed={active === undefined ? undefined : active}
      title={name}
    >
      <span aria-hidden="true">{icon}</span>
    </button>
  );
}
